import React, {
  useCallback,
  useContext,
  useState,
} from 'react';
import { useChannelStateContext } from 'stream-chat-react';
import PropTypes from 'prop-types';

import { PillButton } from '../../../../../components/Button/ActionButtons';
import ChatContext from '../../../../context/ChatContext';
import useToolsNavigation from '../../../../hooks/useToolsNavigation';
import ClientQuickInfoButton from '../../../../components/ClientQuickInfoButton';
import useChannel from '../../hooks/useChannel';
import MessagingChannelProfilePic from '../MessagingChannelProfilePic';
import ChannelDocumentsModal from '../../../../../chat/components/ChannelDocumentsModal';

import {
  SingleChannelNameContainer,
  MultipleChannelNameContainer,
  CloseButtonComponent,
  StyledLink,
  BackButton,
  StyledBackIcon,
  StyledFolderIcon,
} from './styles';
import texts from './texts.json';
import './MessagingChannelHeader.css';

const MessagingChannelHeader = ({
  emptyChat,
}) => {
  const { channel, loading } = useChannelStateContext();
  const { getChannelName } = useChannel(channel);

  const {
    isMultiChannelView,
    quickChatUser,
    onQuickChatUserSelected,
    onMarkChannelAsUnread,
    onMarkChannelAsRead,
    readOnlyMode,
    setChatListOpen,
    isChatModalOpen,
  } = useContext(ChatContext);
  const { navigateToClientPage } = useToolsNavigation();
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);

  const isLoading = typeof loading === 'boolean' ? loading : true;

  const endUserId = channel.id;
  const isOnline = channel.state.members[endUserId]?.user.online || false;
  const endUser = channel.state.members[endUserId]?.user || {};
  const {
    image,
  } = endUser;

  const getTitle = useCallback(() => {
    if (emptyChat) {
      return texts.chat;
    }
    return isLoading ? texts.loadingChat : getChannelName();
  }, [
    emptyChat,
    isLoading,
    getChannelName,
  ]);

  const getChannelHeader = useCallback(() => {
    if (isMultiChannelView) {
      return (
        <MultipleChannelNameContainer>
          {isChatModalOpen && (
            <BackButton onClick={() => setChatListOpen(true)}>
              <StyledBackIcon />
            </BackButton>
          )}
          <MessagingChannelProfilePic image={image} isOnline={isOnline} />
          <StyledLink onClick={() => navigateToClientPage(channel.id)}>
            {getTitle()}
          </StyledLink>
          <ClientQuickInfoButton
            userId={endUserId}
          />
          <PillButton
            size="small"
            onClick={() => setIsDocumentsModalOpen(true)}
            icon={<StyledFolderIcon />}
            variant="info"
          />
        </MultipleChannelNameContainer>
      );
    }
    return (
      <SingleChannelNameContainer>
        {isChatModalOpen && (
          <BackButton onClick={() => setChatListOpen(true)}>
            <StyledBackIcon />
          </BackButton>
        )}
        <MessagingChannelProfilePic image={image} isOnline={isOnline} />
        <StyledLink onClick={() => navigateToClientPage(channel.id)}>
          {getTitle()}
        </StyledLink>
        <ClientQuickInfoButton
          userId={endUserId}
        />
        <PillButton
          size="small"
          onClick={() => setIsDocumentsModalOpen(true)}
          icon={<StyledFolderIcon />}
          variant="info"
        />
      </SingleChannelNameContainer>
    );
  }, [
    isMultiChannelView,
    getTitle,
    navigateToClientPage,
    channel.id,
    setChatListOpen,
    isChatModalOpen,
    image,
    isOnline,
    endUserId,
  ]);

  const closeQuickChat = useCallback(() => {
    onQuickChatUserSelected('');
  }, [
    onQuickChatUserSelected,
  ]);

  const handleClick = useCallback(() => {
    if (channel.data.markedAsUnread) {
      onMarkChannelAsRead(channel);
    } else {
      onMarkChannelAsUnread(channel);
    }
  }, [
    channel,
    onMarkChannelAsRead,
    onMarkChannelAsUnread,
  ]);

  const shouldRenderCloseButton = !isMultiChannelView && quickChatUser;

  return (
    <div className="messaging__channel-header">
      {getChannelHeader()}
      {!readOnlyMode && (
        <PillButton
          onClick={handleClick}
          variant="info"
          size="small"
        >
          {channel.data.markedAsUnread ? texts.markAsRead : texts.markAsUnread}
        </PillButton>
      )}
      {shouldRenderCloseButton && <CloseButtonComponent onClick={closeQuickChat} />}
      <ChannelDocumentsModal
        isOpen={isDocumentsModalOpen}
        onClose={() => setIsDocumentsModalOpen(false)}
        channel={{ userId: endUserId, id: channel.id }}
      />
    </div>
  );
};

MessagingChannelHeader.propTypes = {
  emptyChat: PropTypes.bool,
};

MessagingChannelHeader.defaultProps = {
  emptyChat: false,
};

export default MessagingChannelHeader;
